// The Crafter (MB — reclassified out of "blocked / M25"). PURE (ENG-1).
//
// The 1.21 auto-crafter: a redstone-pulsed 3×3 grid that crafts one output from
// its ingredients and ejects it. The "M25-era" tag was a planning choice, not a
// technical gate — the crafting matcher (game/crafting.matchCrafting) already
// exists, so the craft-attempt + consume logic is buildable today. This owns that
// logic; the host stores the 9 slots (a block entity), fires on the rising edge,
// and ejects the result toward the crafter's facing (reusing the dropper path).
import { matchCrafting } from './crafting';
import { type ItemStack, isEmpty, emptyStack } from './inventory';
import type { Registries } from '../registry/load';

/** A crafter is a 3×3 grid. */
export const CRAFTER_SIZE = 9;

export interface CraftResult {
  /** The crafted output stack. */
  output: ItemStack;
  /** The slots after spending one of each ingredient (an emptied slot → null). */
  slots: (ItemStack | null)[];
}

/**
 * The grid the crafter presents to the matcher. A disabled slot contributes
 * nothing — it is a forced gap the player toggles to shape a recipe (so a single
 * stick can be crafted in a corner without the matcher trimming it into a plank).
 */
export function crafterGrid(slots: readonly (ItemStack | null)[], disabled: readonly boolean[]): ItemStack[] {
  const grid: ItemStack[] = [];
  for (let i = 0; i < CRAFTER_SIZE; i++) {
    const s = slots[i];
    grid.push(disabled[i] || s == null || isEmpty(s) ? emptyStack() : s);
  }
  return grid;
}

/**
 * Attempt one auto-craft. Returns the output plus the slots after consuming one of
 * each ingredient, or null when nothing matches. A successful match means every
 * filled (enabled) cell is part of the recipe (the matcher trims + matches the
 * exact shape), so one item is spent from each.
 */
export function craftOnce(
  reg: Registries,
  slots: readonly (ItemStack | null)[],
  disabled: readonly boolean[],
): CraftResult | null {
  const match = matchCrafting(reg, crafterGrid(slots, disabled), 3);
  if (!match) return null;
  const next: (ItemStack | null)[] = slots.map((s) => (s == null ? null : { ...s }));
  for (let i = 0; i < CRAFTER_SIZE; i++) {
    if (disabled[i]) continue;
    const s = next[i];
    if (s == null || isEmpty(s)) continue;
    next[i] = s.count <= 1 ? null : { ...s, count: s.count - 1 };
  }
  return { output: match.result, slots: next };
}

/** Whether the current grid would craft something (comparator / redstone check). */
export function canCraft(reg: Registries, slots: readonly (ItemStack | null)[], disabled: readonly boolean[]): boolean {
  return matchCrafting(reg, crafterGrid(slots, disabled), 3) != null;
}

/**
 * Toggle a slot's disabled flag. Java only lets you disable an *empty* slot (a
 * filled slot is always an ingredient); returns a new array, unchanged if the
 * toggle isn't allowed.
 */
export function toggleDisabled(disabled: readonly boolean[], i: number, slotEmpty: boolean): boolean[] {
  const out = disabled.slice();
  if (!out[i] && !slotEmpty) return out; // refuse to disable a filled slot
  out[i] = !out[i];
  return out;
}
